<template>
  <div class="bottom-right-cookie-container" v-if="showUpdateNotice">
    <v-card class="update-card" :elevation="10">
      <div class="update-header">
        <div class="update-icon">
          <v-icon icon="mdi-update" color="white" size="large"></v-icon>
        </div>
        <div class="update-title-section">
          <div class="update-title">Update available</div>
          <div class="update-version" v-if="updateInfo">Version {{ updateInfo.version }}</div>
        </div>
      </div>
      
      <div class="update-content">
        <p>A new version of the website is available. Update now to get the latest fixes and improvements.</p>
      </div>

      <div class="update-actions">
        <v-btn
          @click="applyUpdate"
          class="accept-btn"
          :loading="isUpdating"
          :disabled="isUpdating"
        >
          <v-icon icon="mdi-refresh" size="small" class="mr-1"></v-icon>
          {{ isUpdating ? 'Updating...' : 'Update now' }}
        </v-btn>
        <v-btn
          @click="closeUpdateNotice"
          class="later-btn"
          text
          :disabled="isUpdating"
        >
          Later
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { updateStoredVersion } from '../utils/version'

const showUpdateNotice = ref(false)
const updateInfo = ref(null)
const isUpdating = ref(false)
let ignoreNextCheck = false

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const closeUpdateNotice = () => {
  showUpdateNotice.value = false
  // Ignorer la prochaine vérification pour éviter la réouverture immédiate
  ignoreNextCheck = true
  setTimeout(() => {
    ignoreNextCheck = false
  }, 1000)
}

const applyUpdate = async () => {
  if (updateInfo.value && updateInfo.value.version) {
    try {
      isUpdating.value = true
      ignoreNextCheck = true // Empêcher les nouvelles vérifications pendant la mise à jour
      
      // Mettre à jour la version stockée
      updateStoredVersion(updateInfo.value.version)

      // Effacer le cache workbox
      const cacheKeys = await caches.keys()
      for (const key of cacheKeys) {
        if (key.startsWith('workbox-precache')) {
          await caches.delete(key)
          console.log('Cache effacé:', key)
        }
      }

      // Désinscrire les service workers
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      }

      // Recharger la page pour réinstaller le service worker et reconstruire le cache
      window.location.reload()
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error)
      window.location.reload() // Recharger quand même en cas d'erreur
    } finally {
      isUpdating.value = false
      ignoreNextCheck = false
    }
  }
}

const showNotice = (versionInfo) => {
  // Ne pas mettre à jour si on est en train de faire une mise à jour
  // ou si on vient juste de fermer la popup
  if (isUpdating.value || ignoreNextCheck) {
    return
  }

  // Ne pas réinitialiser les infos si la popup est déjà affichée
  if (!showUpdateNotice.value) {
    updateInfo.value = versionInfo
    showUpdateNotice.value = true
  }
}

defineExpose({ showNotice })
</script>

<style scoped>
.bottom-right-cookie-container {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  width: 320px;
  animation: fadeIn 0.4s ease-out;
}

.update-card {
  border-radius: 8px !important;
  width: 100%;
  overflow: hidden;
  background-color: #1a1a2e !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5) !important;
}

.update-header {
  display: flex;
  align-items: center;
  padding: 16px;
}

.update-icon {
  background: var(--tertiary-color);
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  box-shadow: 0 4px 8px rgba(136, 0, 255, 0.4);
}

.update-title-section {
  flex: 1;
}

.update-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
}

.update-version {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.update-content {
  padding: 0 16px 16px;
  color: rgba(255, 255, 255, 0.9);
}

.update-features {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.8);
}

.update-actions {
  display: flex;
  padding: 16px;
  gap: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background-color: rgba(0, 0, 0, 0.2);
}

.accept-btn {
  flex: 1;
  background: var(--tertiary-color) !important;
  color: white !important;
}

.later-btn {
  color: rgba(255, 255, 255, 0.7) !important;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>